import { Button, Input, Modal } from '@quickreport/ui';
import { AlertTriangle, Info } from 'lucide-react';
import { type ReactNode, useId, useState } from 'react';

/** One line of "what will happen". */
export function Consequence({
  children,
  tone = 'plain',
}: {
  children: ReactNode;
  tone?: 'plain' | 'warn';
}) {
  return (
    <li className="flex gap-2">
      {tone === 'warn' ? (
        <AlertTriangle size={15} className="mt-0.5 shrink-0 text-warn" aria-hidden />
      ) : (
        <span
          className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-slate/50"
          aria-hidden="true"
        />
      )}
      <span>{children}</span>
    </li>
  );
}

/**
 * Asks before something is deleted: says what it is, lists what will happen, and for the serious
 * cases asks the person to type DELETE. Cancel is the easy way out; closing the box deletes nothing.
 */
export function DeleteConfirm({
  title,
  subject,
  consequences,
  note,
  confirmLabel,
  typeToConfirm,
  blockedReason,
  onConfirm,
  onClose,
}: {
  title: string;
  /** What is being deleted, shown in a card. */
  subject: ReactNode;
  consequences: ReactNode;
  /** A last reassurance or alternative, under the list. */
  note?: ReactNode;
  confirmLabel: string;
  /** Ask the person to type DELETE before the button works. */
  typeToConfirm: boolean;
  /** When set, the person may not delete; the box only explains. */
  blockedReason: string | null;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const typedId = useId();
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = !blockedReason && (!typeToConfirm || typed.trim().toLowerCase() === 'delete');

  async function go() {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete');
      setBusy(false);
    }
  }

  return (
    <Modal title={title} label={title} onClose={busy ? () => undefined : onClose}>
      <div className="rounded-lg border border-line bg-canvas p-3 text-[13px]">{subject}</div>

      {blockedReason ? (
        <p className="flex gap-2 rounded-lg bg-amber-50 p-3 text-[13px] text-ink" role="status">
          <Info size={16} className="mt-0.5 shrink-0 text-warn" aria-hidden />
          {blockedReason}
        </p>
      ) : (
        <>
          <div>
            <p className="text-[13px] font-medium">If you delete it</p>
            <ul className="mt-2 grid gap-2 text-[13px] text-ink">{consequences}</ul>
          </div>
          {note ? <p className="text-[12px] text-slate">{note}</p> : null}
          {typeToConfirm ? (
            <div className="grid gap-1 text-[13px]">
              <label htmlFor={typedId}>
                To go ahead, type <b>DELETE</b> below
              </label>
              <Input
                id={typedId}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void go()}
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
              />
            </div>
          ) : null}
        </>
      )}

      {error ? (
        <p role="alert" className="text-[13px] text-flag">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap justify-end gap-2">
        <Button onClick={onClose} disabled={busy} autoFocus>
          {blockedReason ? 'Close' : 'Keep it'}
        </Button>
        {blockedReason ? null : (
          <button
            type="button"
            onClick={() => void go()}
            disabled={!ready || busy}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-flag px-3 font-medium text-white shadow-sm transition-colors hover:brightness-95 disabled:pointer-events-none disabled:opacity-40"
          >
            {busy ? 'Deleting…' : confirmLabel}
          </button>
        )}
      </div>
    </Modal>
  );
}
